import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UserService } from 'src/app/services/user.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FieldsService } from 'src/app/services/fields.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { AuthService } from 'src/app/services/auth.service';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-frm-manage-staff',
  templateUrl: './frm-manage-staff.component.html',
  styleUrls: ['./frm-manage-staff.component.css']
})
export class FrmManageStaffComponent implements OnInit {
  private readonly hiddenStudioRoleIds = new Set(['administrator']);

  constructor(
    @Inject(MAT_DIALOG_DATA) public _injectedDialogData: any,
    public _entityRESTService: EntityRESTService,
    public _userRESTService: UserService,
    private dialogRef: MatDialogRef<FrmManageStaffComponent>,
    private _drupalRESTService: DrupalRESTService,
    private _fieldsService: FieldsService,
    public _permissionsService: PermissionsService,
    private _authService: AuthService,
    private _snackBar: MatSnackBar,
  ) { }

  myForm: FormGroup;
  errorMessage: string;
  successMessage: string;
  eckType = "user";
  bundle = "user";
  entityId = this._injectedDialogData?.['data']?.uid;
  showProgressSpinner: boolean = false;
  actionType: string;
  defaultTime: string = '00:00:00';
  formIsPatching: boolean = false; // Initialize the formIsPatching flag
  availableRoles: string[] = [];
  priorEmployment: boolean = false;
  previousSchools: any[] = [];
  selectedSchools: string[] = [];
  hasPassword: boolean = false;

  /** Active state as last confirmed by the server, for reverting a rejected save. */
  serverStatus: boolean | null = null;

  // Studio autocomplete properties
  autoCompleteStudioOptions: any[] = [];
  selectedStudios: any[] = [];
  defaultStudio: any = null;

  // Per-studio role assignment
  studioRoleOptions: { id: string; label: string }[] = [];
  currentStudioRoles: string[] = [];
  hiddenAssignedStudioRoles: string[] = [];
  savingStudioRole = false;

  ngOnInit(): void {
    // this.showProgressSpinner = true;
    LoggerService.log(this._injectedDialogData?.['data']);
    this.eckType = "user";
    this.bundle = "user";
    this.entityId = this._injectedDialogData?.['data']?.uid;

    this.myForm = new FormGroup({
      uid: new FormControl(''),
      mail: new FormControl('', [Validators.required, Validators.email]),
      field_last_name: new FormControl('', Validators.required),
      field_first_name: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required),
      pass: new FormControl('', this.actionType === 'create' ? [] : []),
      confirmPassword: new FormControl(''),
      field_ami_id: new FormControl(''),
      field_birthday: new FormControl(''),
      field_hiring_date: new FormControl(''),
      field_cell_number: new FormControl(''),
      field_studios_data: new FormControl(''),
      field_prior_studio_employment: new FormControl({ value: false, disabled: true }),
      studio_access_list: new FormControl([]),
      field_studio_reference_search: new FormControl(''),
      roles: new FormControl(['Executive', 'Instructor']),
      status: new FormControl(true),
      studio_role: new FormControl([]),
    }, { validators: this.passwordsMatchingValidator });

    this.initializeDialogData();
    this.initializeDefaultStudio();
    this.loadStudioRoleOptions();

    let previousFirstName = '';
    let previousLastName = '';

    // Initialize base roles
    this.availableRoles = ['Executive', 'Instructor', 'Coach'];

    // Add Franchisee role if user has admin access
    if (this._permissionsService.has_access('manage_franchisee_role')) {
      this.availableRoles.push('Franchisee');
    }

    // Subscribe to form value changes with conditions
    this.myForm.valueChanges.pipe(
      debounceTime(1000), // Wait for 1 second of inactivity
      distinctUntilChanged(), // Only emit if the value has changed
      filter(() => {
        const firstName = this.myForm.get('field_first_name').value;
        const lastName = this.myForm.get('field_last_name').value;
        const hasChanged = firstName !== previousFirstName || lastName !== previousLastName;
        previousFirstName = firstName;
        previousLastName = lastName;
        return hasChanged && firstName && lastName;
      })
    ).subscribe(() => {
      // this.OR_lookupStaff();
    });

    // Subscribe to priorEmployment changes
    this.myForm.get('field_prior_studio_employment').valueChanges.subscribe(value => {
      // Skip during form initialization/patching
      if (this.formIsPatching) {
        return;
      }

      const firstName = this.myForm.get('field_first_name').value;
      const lastName = this.myForm.get('field_last_name').value;

      if (value == true && firstName && lastName) {
        this.searchPreviousEmployment();
      } else {
        this.previousSchools = [];
        this.selectedSchools = [];
        this.myForm.get('field_studios_data').setValue('');
        this.myForm.get('field_ami_id').setValue('');
      }
    });

    this.myForm.get('field_first_name').valueChanges.subscribe(() => {
      this.checkNameFieldsAndTogglePriorEmployment();
    });

    this.myForm.get('field_last_name').valueChanges.subscribe(() => {
      this.checkNameFieldsAndTogglePriorEmployment();
    });
  }

  checkNameFieldsAndTogglePriorEmployment() {
    const firstName = this.myForm.get('field_first_name').value;
    const lastName = this.myForm.get('field_last_name').value;

    if (firstName && lastName) {
      this.myForm.get('field_prior_studio_employment').enable();
    } else {
      this.myForm.get('field_prior_studio_employment').disable();
      this.myForm.get('field_prior_studio_employment').setValue(false); // Reset to No if disabled
    }
  }

  initializeDialogData() {
    const dialogData = this._injectedDialogData?.data;
    LoggerService.log('[FrmManageStaff] Injected dialog data:', this._injectedDialogData);
    LoggerService.log('[FrmManageStaff] Extracted dialogData:', dialogData);
    LoggerService.log('[FrmManageStaff] UID from dialogData:', dialogData?.uid);
    if (dialogData) {
      if (dialogData.action) {
        this.actionType = dialogData.action;
      }
      if (dialogData.uid && this.actionType === 'edit') {
        LoggerService.log('[FrmManageStaff] Calling initializeForEdit with UID:', dialogData.uid);
        this.initializeForEdit(dialogData.uid);
      }
    }
  }

  initializeDefaultStudio() {
    // Get the currently selected studio from AuthService
    const currentStudio = this._authService.selectedStudio;
    if (currentStudio && this.actionType !== 'edit') {
      this.defaultStudio = {
        label: currentStudio.name || currentStudio.key,
        value: currentStudio.id
      };
      
      // Add the default studio to selected studios for new staff members
      this.selectedStudios = [this.defaultStudio];
      this.updateStudioReferences();
    }
  }

  onSubmit() {
    if (this.myForm.valid) {
      // Prepare the form data. Status is sent as an explicit Active/Blocked
      // string, but the form control itself keeps its boolean value — writing
      // the string back into the control leaves the toggle matching neither
      // option, so it renders blank and the next submit sends a truthy
      // 'Blocked'.
      const formData = { ...this.myForm.value };
      formData.status = this.myForm.get('status').value ? 'Active' : 'Blocked';

      // Ensure studio_access_list is properly set
      formData.studio_access_list = this.selectedStudios.map(studio => studio.value);

      // Remove fields not needed for user entity submission.
      delete formData.field_studio_reference_search;
      delete formData.studio_role;

      this._userRESTService.createUser(formData).subscribe(
        response => {
          this.myForm.reset();
          this.selectedStudios = [];
          this.dialogRef.close();

          // Refresh instructors and executives
          this._fieldsService.fetchAndSetInstructorData().then(data => {
          }).catch(error => {
          });
        },
        error => {
          if (error.error && error.error.message) {
            this.errorMessage = error.error.message;
          } else {
            this.errorMessage = "Failed to create user due to an unknown error.";
          }
        }
      );
    }
  }

  initializeForEdit(userId: number) {
    // Mask the blank form while the record loads, otherwise the edit dialog
    // opens with empty required fields and a disabled submit button.
    this.showProgressSpinner = true;
    LoggerService.log('[FrmManageStaff] initializeForEdit called with userId:', userId);
    this._entityRESTService.getEntity(this.eckType, this.bundle, userId).subscribe(
      response => {
        LoggerService.log('[FrmManageStaff] API response for user:', response);
        this.hasPassword = !!response['pass'];
        // Remove the password field from the response.
        delete response?.['pass'];
        // Convert 'status' to boolean.
        response['status'] = response['status'] === '1' ? true : false;
        // Remember the stored state so a rejected save can be rolled back.
        this.serverStatus = response['status'];
        // Ensure roles is an array.
        response['roles'] = Array.isArray(response['roles']) ? response['roles'] : [response['roles']];
        // Capitalize the roles for display in the template
        response['roles'] = response['roles'].map((role: string) => role.charAt(0).toUpperCase() + role.slice(1));

        // Handle studio references
        if (response['studio_access_list']) {
          this.loadStudiosForEdit(response['studio_access_list']);
        } else if (response['field_studio_reference']) {
          // Fallback for existing data that might still use the old field name
          this.loadStudiosForEdit(response['field_studio_reference']);
        }

        // Prevent value change subscriptions from triggering during form patching
        this.formIsPatching = true;
        this.myForm.patchValue(response);
        this.showProgressSpinner = false;
        this.myForm.patchValue({ field_ami_id: response['field_ami_id'].toString() });
        this.formIsPatching = false;

        // Load the user's per-studio role assignment.
        this.loadStudioRoleForUser(userId);
      },
      error => {
        this.errorMessage = "Failed to load user: " + error.message; // Set error message
        this.showProgressSpinner = false;
      }
    );
  }

  onUpdate() {
    if (this.myForm.valid) {
      // Clear previous messages
      this.errorMessage = '';
      this.successMessage = '';

      const userId = this.myForm.get('uid').value;
      const updateData = { ...this.myForm.value };

      if (!updateData.pass) {
        delete updateData.pass;
      }

      // Ensure studio_access_list is properly set
      updateData.studio_access_list = this.selectedStudios.map(studio => studio.value);

      // Remove fields not needed for user entity submission.
      delete updateData.field_studio_reference_search;
      delete updateData.studio_role;

              // Debug logging
        LoggerService.log('Selected studios:', this.selectedStudios);
        LoggerService.log('Studio IDs being sent:', updateData.studio_access_list);
        LoggerService.log('Full update data:', updateData);

      this._userRESTService.updateUser(userId, updateData).subscribe(
        response => {
          // Handle successful update
          this.successMessage = response.message; // Set success message from response
          this.errorMessage = ''; // Clear any previous error messages
          // The submitted state is now the stored state.
          this.serverStatus = !!this.myForm.get('status').value;
          // Optionally, you can reset the form or close the dialog here
          // this.myForm.reset();
          // this.dialogRef.close();

          // Refresh instructors and executives
          this._fieldsService.fetchAndSetInstructorData().then(data => {
          }).catch(error => {
          });
        },
        error => {
          this.successMessage = ''; // Clear any success messages
          if (error.error && error.error.message) {
            this.errorMessage = error.error.message;
          } else {
            this.errorMessage = "Failed to update user: " + error.message;
          }

          // The save was rejected, so nothing changed on the server. Put the
          // Is Active toggle back to the stored value rather than leaving it
          // showing a state the account is not actually in.
          if (this.serverStatus !== null) {
            this.myForm.get('status').setValue(this.serverStatus, { emitEvent: false });
          }
        }
      );
    }
  }

  onDelete(userId: number) {
    this._userRESTService.deleteUser(userId).subscribe(
      response => {
        // Handle successful deletion
      },
      error => {
        this.errorMessage = "Failed to delete user: " + error.message; // Set error message
      }
    );
  }

  OR_lookupStaff() {
    // this.showProgressSpinner = true;
    this.errorMessage = ''; // Clear any previous error message

    // Extract first name and last name from the form
    const firstName = this.myForm.get('field_first_name').value;
    const lastName = this.myForm.get('field_last_name').value;

    // Check if both first name and last name are provided
    if (!firstName || !lastName) {
      // Optionally, set an error message if needed
      this.errorMessage = 'Please enter both first name and last name to search.';
      return;
    }

    // If the AMI ID is already present, skip the search.
    if (this.myForm.get('field_ami_id').value) {
      LoggerService.log('AMI ID already present. Skipping search.');
      return;
    }

    const params = [
      { parameter: 'field_first_name', value: firstName },
      { parameter: 'field_last_name', value: lastName }
    ];

    this._drupalRESTService.httpGET('/api_rest/v1/OR_lookupStaff', params).subscribe({
      next: (response: any[]) => {
        this.showProgressSpinner = false;
        if (response && response.length > 0) {
          const staffData = response[0];

          this.formIsPatching = true; // Disable value changes subscription
          // Update the form with the returned data
          const patchValue: any = {
            field_ami_id: staffData.id || '',
          };

          // Only patch field_cell_number if it's not already filled out
          if (!this.myForm.get('field_cell_number').value) {
            // Do not patch phone number.
            // patchValue.field_cell_number = staffData.studios?.[0]?.phone || '';
          }

          // Only patch mail if it's not already filled out
          if (!this.myForm.get('mail').value) {
            patchValue.mail = staffData.email || '';
          }

          this.myForm.patchValue(patchValue);
          this.formIsPatching = false; // Re-enable value changes subscription
        } else {
          // this.errorMessage = "No staff found with the given details.";
          // Clear out AMI ID and cell number if no staff found.
          this.myForm.patchValue({
            field_ami_id: '',
            field_cell_number: '',
          });
        }
      },
      error: (error) => {
        this.showProgressSpinner = false;
        if (error?.error?.message?.includes('Failed to Get Access Token')) {
          this.errorMessage = 'Unable to connect to AMI. Please contact an administrator to verify the AMI token settings.';
        } else {
          this.errorMessage = "Failed to search for staff: " + error.message;
        }
      }
    });
  }

  passwordsMatchingValidator(formGroup: FormGroup): { [s: string]: boolean } | null {
    const password = formGroup.get('pass').value;
    const confirmPassword = formGroup.get('confirmPassword').value;
    if (password !== confirmPassword) {
      return { 'passwordMismatch': true };
    }
    return null;
  }

  searchPreviousEmployment() {
    const firstName = this.myForm.get('field_first_name').value;
    const lastName = this.myForm.get('field_last_name').value;
    const field_ami_id = this.myForm.get('field_ami_id').value;

    // Ensure the field_prior_studio_employment is true before proceeding
    const priorEmployment = this.myForm.get('field_prior_studio_employment').value;

    if (!priorEmployment) {
      return; // Exit early if prior employment is not set to true
    }

    if (!firstName || !lastName) {
      this.errorMessage = 'Please enter both first name and last name to search for prior employment.';
      return;
    }

    this.showProgressSpinner = true;
    const params = [
      { parameter: 'field_first_name', value: firstName },
      { parameter: 'field_last_name', value: lastName }
    ];

    this._drupalRESTService.httpGET('/api_rest/v1/OR_lookupStaff', params).subscribe({
      next: (response: any[]) => {
        this.showProgressSpinner = false;
        if (response && response.length > 0 && response[0].studios) {
          // Store the school details
          this.previousSchools = response[0].studios.map(studio => ({
            name: studio.name,
            city: studio.city,
            state: studio.state,
            phone: studio.phone,
            website: studio.website
          }));

          // Set the field_ami_id on the form.
          this.myForm.get('field_ami_id').setValue(response[0].id);

          const schoolNames = response[0].studios.map(studio => studio.name);
          this.previousSchools = schoolNames.join(', ');

          if (this.previousSchools.length === 0) {
            this.errorMessage = "No previous employment found for this staff member.";
          }
        } else {
          this.previousSchools = [];
          this.errorMessage = "No previous employment found for this staff member.";
        }
      },
      error: (error) => {
        this.showProgressSpinner = false;
        if (error?.error?.message?.includes('Failed to Get Access Token')) {
          this.errorMessage = 'Unable to connect to AMI. Please contact your administrator to verify the AMI token settings.';
        } else {
        this.errorMessage = "Failed to search for previous employment: " + error.message;
        this.errorMessage = "Failed to search for previous employment: " + error.message;
          this.errorMessage = "Failed to search for previous employment: " + error.message;
        }
      }
    });
  }

  toggleSchoolSelection(schoolId: string) {
    const index = this.selectedSchools.indexOf(schoolId);
    if (index > -1) {
      this.selectedSchools.splice(index, 1);
    } else {
      this.selectedSchools.push(schoolId);
    }
    this.updatePreviousEmploymentField();
  }

  updatePreviousEmploymentField() {
    const selectedSchoolNames = this.previousSchools
      .filter(school => this.selectedSchools.includes(school.id))
      .map(school => `${school.name} (${school.city}, ${school.state})`)
      .join(', ');
    this.myForm.get('field_studios_data').setValue(selectedSchoolNames);
  }

    /**
   * Handle studio autocomplete input
   */
  onAutocompleteStudio($event: { target: { value: string; }; }) {
    let term: string = $event.target.value;

    if (term.length < 2) {
      this.autoCompleteStudioOptions = [];
      return;
    }

    // Load all studios first, then filter by term
    this._drupalRESTService.httpGET("/api_rest/v1/loadStudioList")
      .subscribe((studios: any) => {
        if (Array.isArray(studios)) {
          this.autoCompleteStudioOptions = studios
            .filter(studio =>
              (studio.name && studio.name.toLowerCase().includes(term.toLowerCase())) ||
              (studio.key && studio.key.toLowerCase().includes(term.toLowerCase()))
            )
            .map(studio => ({
              label: studio.name || studio.key,
              value: studio.id
            }));
        }
      })
  }

  /**
   * Handle studio selection from autocomplete
   */
  onStudioSelected($event: any) {
    const selectedStudio = this.autoCompleteStudioOptions.find(
      option => option.label === $event.option.value
    );

    if (selectedStudio) {
      // Check if studio is already selected
      const alreadySelected = this.selectedStudios.find(
        studio => studio.value === selectedStudio.value
      );

      if (!alreadySelected) {
        this.selectedStudios.push(selectedStudio);
        this.updateStudioReferences();
      }
    }

    // Clear the search input
    this.myForm.get('field_studio_reference_search').setValue('');
  }

  /**
   * Remove a studio from selection
   */
  removeStudio(studioToRemove: any) {
    // Prevent removing the default studio for new staff members
    if (this.defaultStudio && studioToRemove.value === this.defaultStudio.value && this.actionType !== 'edit') {
      return;
    }
    
    this.selectedStudios = this.selectedStudios.filter(
      studio => studio.value !== studioToRemove.value
    );
    this.updateStudioReferences();
  }

  /**
   * Update the form control with selected studio IDs
   */
  updateStudioReferences() {
    const studioIds = this.selectedStudios.map(studio => studio.value);
    // Always send as array, even for single values
    this.myForm.get('studio_access_list').setValue(studioIds.length > 0 ? studioIds : []);
  }

  /**
   * Load available studio role options from the permissions config.
   */
  loadStudioRoleOptions(): void {
    this._drupalRESTService.httpGET('/api/studio-permissions/config').subscribe({
      next: (res: any) => {
        if (res?.success && res.data?.roles) {
          this.studioRoleOptions = Object.entries(res.data.roles)
            .map(([id, def]: [string, any]) => ({ id, label: def.label, weight: def.weight ?? 0 }))
            .filter(({ id }) => this.isVisibleStudioRole(id))
            .sort((a, b) => a.weight - b.weight);
        }
      },
    });
  }

  /**
   * Load the staff member's current studio roles for the selected studio.
   */
  loadStudioRoleForUser(uid: number): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) return;

    this._drupalRESTService.httpGET(`/api/studio-permissions/studio/${studioId}/assignments`).subscribe({
      next: (res: any) => {
        if (res?.success && res.data) {
          const match = res.data.find((a: any) => +a.uid === +uid);
          this.currentStudioRoles = match?.roles || [];
          this.hiddenAssignedStudioRoles = this.currentStudioRoles.filter(roleId => !this.isVisibleStudioRole(roleId));
          this.myForm.get('studio_role').setValue(
            this.currentStudioRoles.filter(roleId => this.isVisibleStudioRole(roleId))
          );
        }
      },
    });
  }

  /**
   * Save studio role assignments when the multi-select changes.
   */
  onStudioRolesChange(newRoles: string[]): void {
    const uid = this.entityId;
    const studioId = this._authService.selectedStudio?.id;
    if (!uid || !studioId) return;

    this.savingStudioRole = true;
    const rolesToSave = [...new Set([...(newRoles || []), ...this.hiddenAssignedStudioRoles])];

    if (rolesToSave.length > 0) {
      this._drupalRESTService.httpPOST(
        `/api/studio-permissions/studio/${studioId}/assign`,
        { uid: +uid, roles: rolesToSave }
      ).subscribe({
        next: (res: any) => {
          this.savingStudioRole = false;
          if (res?.success) {
            this.currentStudioRoles = rolesToSave;
            this._snackBar.open('Studio roles updated.', 'Close', { duration: 3000 });
            this._permissionsService.loadStudioPermissions(true);
          } else {
            this._snackBar.open(res?.message || 'Failed to assign roles.', 'Close', { duration: 5000 });
            this.myForm.get('studio_role').setValue(
              this.currentStudioRoles.filter(roleId => this.isVisibleStudioRole(roleId))
            );
          }
        },
        error: (err: any) => {
          this.savingStudioRole = false;
          this._snackBar.open(err?.error?.message || 'Error assigning roles.', 'Close', { duration: 5000 });
          this.myForm.get('studio_role').setValue(
            this.currentStudioRoles.filter(roleId => this.isVisibleStudioRole(roleId))
          );
        },
      });
    } else {
      // Empty selection → remove all role assignments.
      this._drupalRESTService.httpPOST(
        `/api/studio-permissions/studio/${studioId}/remove`,
        { uid: +uid }
      ).subscribe({
        next: (res: any) => {
          this.savingStudioRole = false;
          if (res?.success) {
            this.currentStudioRoles = [];
            this.hiddenAssignedStudioRoles = [];
            this._snackBar.open('Studio roles removed.', 'Close', { duration: 3000 });
            this._permissionsService.loadStudioPermissions(true);
          } else {
            this._snackBar.open(res?.message || 'Failed to remove roles.', 'Close', { duration: 5000 });
            this.myForm.get('studio_role').setValue(
              this.currentStudioRoles.filter(roleId => this.isVisibleStudioRole(roleId))
            );
          }
        },
        error: (err: any) => {
          this.savingStudioRole = false;
          this._snackBar.open(err?.error?.message || 'Error removing roles.', 'Close', { duration: 5000 });
          this.myForm.get('studio_role').setValue(
            this.currentStudioRoles.filter(roleId => this.isVisibleStudioRole(roleId))
          );
        },
      });
    }
  }

  private isVisibleStudioRole(roleId: string): boolean {
    return !this.hiddenStudioRoleIds.has((roleId || '').toLowerCase());
  }

    /**
   * Load studios for editing existing user
   */
  loadStudiosForEdit(studioReferences: any) {
    if (!studioReferences) {
      return;
    }

    // Handle different response formats from the API
    let studiesToLoad: any[] = [];

    if (Array.isArray(studioReferences)) {
      // If it's an array of studio objects or IDs
      studiesToLoad = studioReferences;
    } else if (typeof studioReferences === 'object' && studioReferences.id) {
      // If it's a single studio object (as shown in the API response)
      studiesToLoad = [studioReferences];
    } else {
      // If it's a single ID
      studiesToLoad = [{ id: studioReferences }];
    }

    studiesToLoad.forEach(studioRef => {
      // Extract studio info from the reference
      let studioData: any;

      if (studioRef.id && studioRef.title) {
        // Studio object with full data (like from the API response)
        studioData = {
          label: studioRef.title,
          value: studioRef.id
        };
      } else if (studioRef.id) {
        // Studio object with just ID, need to load the name
        studioData = {
          label: `Studio ${studioRef.id}`, // Temporary label
          value: studioRef.id
        };
      } else {
        // Fallback for just ID
        studioData = {
          label: `Studio ${studioRef}`,
          value: studioRef
        };
      }

      // Check if studio is not already selected
      if (!this.selectedStudios.find(s => s.value == studioData.value)) {
        this.selectedStudios.push(studioData);
      }
    });

    // Update the form control
    this.updateStudioReferences();
  }

}
